import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames/bind';
import type { Appointment, DoctorPatientRelation, Practice } from '../../../../types/ninox.types';
import { getLocalizedDate, getLocalizedDateTime } from '../../../../utils';
import { emptyAppointment } from '../../../../utils/emptyModels';
import FormDialog from '../../FormDialog/FormDialog';
import FormField from '../../FormField/FormField';
import style from '../Modal.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { patientInfoSliceSelector, setPatientInfoUpdate } from '../../../../features/patientInfo/patientInfoSlice';
import _ from 'lodash';
import { createPatientAppointment, fetchDoctorAppointments, fetchDoctorPractices } from '../../../../utils/apiCalls';
import { toast } from 'react-toastify';

const cx = classNames.bind(style);

export type AppointmentModalProps = {
  type: 'appointment';
  show: boolean;
  setShow: (show: boolean) => void;
  appointment: Appointment | null;
  patientId: number;
  doctors: DoctorPatientRelation[];
};

function getLocalISOString(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function getMinStartDate(): string {
  const min = new Date();
  min.setMilliseconds(0);
  min.setSeconds(0);
  // Round up to the next 5-minute interval
  const minutes = min.getMinutes();
  min.setMinutes(Math.ceil(minutes / 5) * 5);
  return getLocalISOString(min);
}

export function AppointmentModal(props: AppointmentModalProps): JSX.Element {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [doctorAppointments, setDoctorAppointments] = useState<Appointment[]>([]);
  const [doctorPractices, setDoctorPractices] = useState<Practice[]>([]);
  const [doctorDateAppointments, setDoctorDateAppointments] = useState<Appointment[]>([]);
  const minStartDate = useMemo(getMinStartDate, []);
  const [appointment, setAppointment] = useState<Appointment>({ ...emptyAppointment, startDate: minStartDate });
  const [onlineFirstAppointmentLength, setOnlineFirstAppointmentLength] = useState<'20' | '30'>('30');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { patientInfoUpdate } = useSelector(patientInfoSliceSelector);

  const filterDoctorAppointmentsForDate = (appointments: Appointment[]): Appointment[] => {
    if (!appointments) {
      return [];
    }
    return appointments.filter(
      (doctorAppointment) =>
        getLocalizedDate(intl, doctorAppointment.startDate) === getLocalizedDate(intl, appointment.startDate)
    );
  };

  const createAppointment = () => {
    // Validation checks for required fields
    if (appointment.type === 'offline' && appointment.practiceId === 0) {
      setErrorMessage('Keine Praxis gewählt');
      return;
    }
    if (appointment.doctorId === 0) {
      setErrorMessage('Kein Arzt gewählt');
      return;
    }
    if (appointment.startDate === '') {
      setErrorMessage('Kein Startdatum gewählt');
      return;
    }

    // Set doctor name for appointment
    props.doctors.forEach((doctor) => {
      if (doctor.doctorId === appointment.doctorId) {
        appointment.doctorName = `${doctor.doctor?.firstname} ${doctor.doctor?.lastname}`;
      }
    });

    const startDate = new Date(appointment.startDate);
    const endDate = new Date(startDate); // New instance because we modify it.
    const appointmentLength =
      appointment.type === 'offline'
        ? appointment.appointmentType === 'followUpAppointment'
          ? 10
          : 15
        : appointment.appointmentType === 'followUpAppointment'
          ? 15
          : Number(onlineFirstAppointmentLength);
    endDate.setMinutes(endDate.getMinutes() + appointmentLength);

    // Call API to create the appointment
    createPatientAppointment({
      ...appointment,
      startDate: startDate.toISOString(), // Convert start/end date to ISO format in with `z` to indicate UTC.
      endDate: endDate.toISOString(), // Convert start/end date to ISO format in with `z` to indicate UTC.
    })
      .then((response) => {
        // Check if the response indicates an error
        if (response.response?.status === 400) {
          const errorMessage = response.response.data.message;
          // Check for "Appointment collision" message
          if (errorMessage.includes('Appointment collision')) {
            setErrorMessage('Terminkollision. Bitte überprüfen Sie die Angaben.');
          }
          // Prevent the modal from closing in case of an error
          return;
        }

        props.setShow(false);

        // Reset state after successful creation
        setAppointment({ ...emptyAppointment, patientId: props.patientId });
        setOnlineFirstAppointmentLength('30');
        appointment.id = '';
        appointment.type = appointment.type === 'online' ? '1' : '2';
        appointment.startDate = startDate.toISOString();
        appointment.endDate = endDate.toISOString();

        // Check if `patientInfoUpdate.appointment` is an array, and handle accordingly
        let updatedAppointments = [];
        if (Array.isArray(patientInfoUpdate.appointment)) {
          updatedAppointments = _.cloneDeep(patientInfoUpdate.appointment);
        }

        // Add the newly created appointment
        updatedAppointments.push(appointment);

        // Update Redux store with the new appointment information
        dispatch(setPatientInfoUpdate({ ...patientInfoUpdate, appointment: updatedAppointments }));

        //show success message
        toast.success('Termin wurde erstellt.');

        //clear error message
        setErrorMessage('');
      })
      .catch((err) => {
        // Catching network or unexpected errors
        console.error('Network or unexpected error while creating appointment:', err);
        setErrorMessage('Netzwerkfehler: Bitte versuchen Sie es später noch einmal.');
      });
  };

  useEffect(() => {
    if (props.appointment) {
      setAppointment(props.appointment);
      if (
        props.appointment.type === 'online' &&
        props.appointment.appointmentType === 'firstAppointment' &&
        props.appointment.startDate &&
        props.appointment.endDate
      ) {
        const start = new Date(props.appointment.startDate);
        const end = new Date(props.appointment.endDate);
        const diffInMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
        setOnlineFirstAppointmentLength(diffInMinutes === 20 ? '20' : '30');
      }
    }
  }, [props.appointment]);

  useEffect(() => {
    fetchDoctorAppointments(appointment.doctorId).then((response) => {
      if (response.error) {
        return;
      }

      if (!response.data) {
        setDoctorAppointments([]);
        setDoctorDateAppointments([]);
      } else {
        setDoctorAppointments(response.data);
        setDoctorDateAppointments(filterDoctorAppointmentsForDate(response.data));
      }
    });
    fetchDoctorPractices(appointment.doctorId).then((response) => {
      if (response.error) {
        return;
      }
      setDoctorPractices(response.data);
    });
  }, [appointment.doctorId]);

  useEffect(() => {
    if (appointment.startDate) {
      setDoctorDateAppointments(filterDoctorAppointmentsForDate(doctorAppointments));
    }
  }, [appointment.startDate]);

  useEffect(() => {
    setAppointment({ ...appointment, patientId: props.patientId });
  }, [props.patientId]);

  const listClasses = cx({ listHeight: true });
  const errorMessageClasses = cx({ errorMessage: true });

  return (
    <FormDialog
      title={intl.formatMessage({ id: 'createAppointment' })}
      onSave={() => createAppointment()}
      onClose={() => props.setShow(false)}
      setShow={() => props.setShow(false)}
      show={props.show}
    >
      <FormField
        type='select'
        name='doctorId'
        value={appointment.doctorId.toString()}
        onChange={(event) => setAppointment({ ...appointment, doctorId: Number(event.target.value) })}
        choices={[['0', '']].concat(
          props.doctors.map((doctor) => [
            doctor.doctorId?.toString() || '0',
            `${doctor.doctor?.title ? intl.formatMessage({ id: doctor.doctor.title }) : ''}
            ${doctor.doctor?.firstname} ${doctor.doctor?.lastname}`,
          ])
        )}
      >
        <FormattedMessage id='doctor' />
      </FormField>
      <FormField
        type='select'
        name='type'
        value={appointment.type}
        onChange={(event) => setAppointment({ ...appointment, type: event.target.value })}
        choices={[
          ['online', 'Online'],
          ['offline', 'Offline'],
        ]}
      >
        <FormattedMessage id='appointmentType' />
      </FormField>
      {appointment.type === 'offline' && appointment.doctorId !== 0 && (
        <FormField
          type='select'
          name='type'
          value={appointment.practiceId.toString()}
          onChange={(event) => setAppointment({ ...appointment, practiceId: Number(event.target.value) })}
          choices={[['0', '']].concat(
            doctorPractices.map((practice) => [practice.id?.toString() || '0', `${practice.name}`])
          )}
        >
          <FormattedMessage id='practice' />
        </FormField>
      )}
      <FormField
        type='select'
        name='appointmentSubtype'
        value={appointment.appointmentType}
        onChange={(event) => setAppointment({ ...appointment, appointmentType: event.target.value })}
        choices={[
          [
            'firstAppointment',
            `Erstgespräch (${appointment.type === 'offline' ? '15m' : `${onlineFirstAppointmentLength}m`})`,
          ],
          ['followUpAppointment', `Folgetermin (${appointment.type === 'offline' ? '10m' : '15m'})`],
        ]}
      >
        <FormattedMessage id='appointmentSubtype' />
      </FormField>
      {appointment.type === 'online' && appointment.appointmentType === 'firstAppointment' && (
        <FormField
          type='select'
          name='firstAppointmentLength'
          value={onlineFirstAppointmentLength}
          onChange={(event) => setOnlineFirstAppointmentLength(event.target.value === '20' ? '20' : '30')}
          choices={[
            ['20', 'Erstgespräch (20m)'],
            ['30', 'Erstgespräch (30m)'],
          ]}
        >
          <FormattedMessage id='appointmentLength' />
        </FormField>
      )}
      <FormField
        type='datetime-local'
        name='startDate'
        step={300}
        min={minStartDate}
        value={appointment.startDate}
        onChange={(event) => setAppointment({ ...appointment, startDate: event.target.value })}
      >
        <FormattedMessage id='startData' />
      </FormField>
      <FormField
        type='textarea'
        placeholder={intl.formatMessage({ id: 'patient.enterRemark' })}
        name='comment'
        value={appointment.comment}
        onChange={(event) => setAppointment({ ...appointment, comment: event.target.value })}
      >
        <FormattedMessage id='patient.message' />
      </FormField>
      <div>
        <FormattedMessage id='doctorAppointments' />:
      </div>
      <div className={listClasses}>
        {doctorDateAppointments.length === 0 && <FormattedMessage id='noAppointments' />}
        {doctorDateAppointments.map((doctorAppointment) => (
          <div key={doctorAppointment.id}>
            {getLocalizedDateTime(intl, doctorAppointment.startDate)} <b>-</b>
            {getLocalizedDateTime(intl, doctorAppointment.endDate)}
          </div>
        ))}
      </div>
      <div className={errorMessageClasses}>{errorMessage}</div>
    </FormDialog>
  );
}
